// frontend/src/pages/AvaliacaoPage.jsx

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatDate, formatPhone } from '../utils/dateUtils';
import {
  User, Phone, Calendar, Activity, ClipboardList, ChevronRight,
  ChevronLeft, Save, FileText, Trash2, Edit3, Plus, Check, X, Search
} from 'lucide-react';

const STEPS = [
  { id: 1, label: 'Dados iniciais' },
  { id: 2, label: 'Queixa principal' },
  { id: 3, label: 'Avaliação física' },
  { id: 4, label: 'Diagnóstico' },
  { id: 5, label: 'Plano terapêutico' },
  { id: 6, label: 'Finalização' },
];

const emptyForm = {
  dataAvaliacao: new Date().toISOString().split('T')[0],
  queixaPrincipal: '',
  historicoDoenca: '',
  avaliacaoPostural: '',
  amplitudeMovimento: '',
  forcaMuscular: '',
  testesEspeciais: '',
  diagnosticoFisioterapeutico: '',
  planoCuidados: '',
  observacoes: '',
};

function calcularIdade(dateOfBirth) {
  if (!dateOfBirth) return null;
  const datePart = String(dateOfBirth).split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const hoje = new Date();
  let age = hoje.getFullYear() - year;
  if (hoje.getMonth() + 1 < month || (hoje.getMonth() + 1 === month && hoje.getDate() < day)) age--;
  return age;
}

export default function AvaliacaoPage() {
  const [pacientes, setPacientes] = useState([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editando, setEditando] = useState(false);
  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPacientes, setFilteredPacientes] = useState([]);

  const [searchParams] = useSearchParams();

  useEffect(() => { buscarPacientes(); }, []);

  // Auto-seleciona paciente se vier via ?patientId=
  useEffect(() => {
    const patientId = searchParams.get('patientId');
    if (patientId && pacientes.length > 0) {
      const p = pacientes.find(p => p._id === patientId);
      if (p) handleSelecionarPaciente(p._id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pacientes]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPacientes([]);
    } else {
      setFilteredPacientes(
        pacientes.filter(p => p.nome.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
  }, [searchTerm, pacientes]);

  const buscarPacientes = async () => {
    try {
      const response = await api.get('/patients');
      setPacientes(response.data.data);
    } catch {
      toast.error('Erro ao carregar pacientes');
    }
  };

  const handleSelecionarPaciente = async (pacienteId) => {
    setPacienteSelecionado(pacienteId);
    setSearchTerm('');
    setFilteredPacientes([]);
    setStep(1);
    limparFormulario();
    try {
      const response = await api.get(`/avaliacoes/paciente/${pacienteId}`);
      setAvaliacoes(response.data.data || []);
    } catch {
      setAvaliacoes([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!pacienteSelecionado) return toast.error('Selecione um paciente');
    setLoading(true);
    try {
      if (editando && avaliacaoSelecionada) {
        await api.put(`/avaliacoes/${avaliacaoSelecionada._id}`, { ...formData, pacienteId: pacienteSelecionado });
        toast.success('Avaliação atualizada!');
      } else {
        await api.post('/avaliacoes', { ...formData, pacienteId: pacienteSelecionado });
        toast.success('Avaliação salva!');
      }
      limparFormulario();
      handleSelecionarPaciente(pacienteSelecionado);
    } catch {
      toast.error('Erro ao salvar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (avaliacao) => {
    setFormData({
      dataAvaliacao: avaliacao.dataAvaliacao?.split('T')[0] || emptyForm.dataAvaliacao,
      queixaPrincipal: avaliacao.queixaPrincipal || '',
      historicoDoenca: avaliacao.historicoDoenca || '',
      avaliacaoPostural: avaliacao.avaliacaoPostural || '',
      amplitudeMovimento: avaliacao.amplitudeMovimento || '',
      forcaMuscular: avaliacao.forcaMuscular || '',
      testesEspeciais: avaliacao.testesEspeciais || '',
      diagnosticoFisioterapeutico: avaliacao.diagnosticoFisioterapeutico || '',
      planoCuidados: avaliacao.planoCuidados || '',
      observacoes: avaliacao.observacoes || '',
    });
    setAvaliacaoSelecionada(avaliacao);
    setEditando(true);
    setStep(1);
  };

  const handleDeletar = async (avaliacaoId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta avaliação?')) return;
    try {
      await api.delete(`/avaliacoes/${avaliacaoId}`);
      toast.success('Avaliação deletada!');
      handleSelecionarPaciente(pacienteSelecionado);
    } catch {
      toast.error('Erro ao deletar avaliação');
    }
  };

  const limparFormulario = () => {
    setFormData(emptyForm);
    setEditando(false);
    setAvaliacaoSelecionada(null);
    setStep(1);
  };

  const paciente = pacientes.find(p => p._id === pacienteSelecionado);
  const idade = paciente ? calcularIdade(paciente.dateOfBirth) : null;

  const isStepDone = (s) => !editando && s < step;

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <SectionHeader icon={<ClipboardList size={20} />} title="Dados da Avaliação" subtitle="Informações iniciais sobre o estado atual do paciente" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Data da Avaliação">
                <input type="date" name="dataAvaliacao" value={formData.dataAvaliacao} onChange={handleChange} required className={inputClass} />
              </Field>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <SectionHeader icon={<Activity size={20} />} title="Queixa e Histórico" subtitle="Registre a queixa principal e o histórico da doença" />
            <Field label="Queixa Principal" required>
              <textarea name="queixaPrincipal" value={formData.queixaPrincipal} onChange={handleChange} placeholder="Descreva a queixa principal do paciente" className={`${inputClass} resize-none`} rows={4} />
            </Field>
            <Field label="História da Doença">
              <textarea name="historicoDoenca" value={formData.historicoDoenca} onChange={handleChange} placeholder="Histórico e evolução da doença" className={`${inputClass} resize-none`} rows={4} />
            </Field>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <SectionHeader icon={<Activity size={20} />} title="Avaliação Física" subtitle="Exame físico detalhado do paciente" />
            <Field label="Avaliação Postural">
              <textarea name="avaliacaoPostural" value={formData.avaliacaoPostural} onChange={handleChange} placeholder="Descrição da postura, alinhamento corporal, simetrias e assimetrias" className={`${inputClass} resize-none`} rows={3} />
            </Field>
            <Field label="Amplitude de Movimento">
              <textarea name="amplitudeMovimento" value={formData.amplitudeMovimento} onChange={handleChange} placeholder="Amplitude de movimento das articulações afetadas" className={`${inputClass} resize-none`} rows={3} />
            </Field>
            <Field label="Força Muscular">
              <textarea name="forcaMuscular" value={formData.forcaMuscular} onChange={handleChange} placeholder="Avaliação de força muscular (escala 0-5)" className={`${inputClass} resize-none`} rows={3} />
            </Field>
            <Field label="Testes Especiais">
              <textarea name="testesEspeciais" value={formData.testesEspeciais} onChange={handleChange} placeholder="Resultados de testes especiais e palpação" className={`${inputClass} resize-none`} rows={3} />
            </Field>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <SectionHeader icon={<FileText size={20} />} title="Diagnóstico" subtitle="Diagnóstico fisioterapêutico baseado na avaliação" />
            <Field label="Diagnóstico Fisioterapêutico" required>
              <textarea name="diagnosticoFisioterapeutico" value={formData.diagnosticoFisioterapeutico} onChange={handleChange} placeholder="Diagnóstico fisioterapêutico baseado na avaliação" className={`${inputClass} resize-none`} rows={5} />
            </Field>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <SectionHeader icon={<ClipboardList size={20} />} title="Plano Terapêutico" subtitle="Definição dos objetivos e condutas do tratamento" />
            <Field label="Plano de Cuidados">
              <textarea name="planoCuidados" value={formData.planoCuidados} onChange={handleChange} placeholder="Plano de tratamento e objetivos terapêuticos" className={`${inputClass} resize-none`} rows={6} />
            </Field>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6">
            <SectionHeader icon={<Check size={20} />} title="Finalização" subtitle="Observações finais e confirmação da avaliação" />
            <Field label="Observações Adicionais">
              <textarea name="observacoes" value={formData.observacoes} onChange={handleChange} placeholder="Observações ou notas adicionais" className={`${inputClass} resize-none`} rows={4} />
            </Field>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 space-y-3">
              <p className="text-sm font-semibold text-blue-800 mb-2">Resumo da Avaliação</p>
              {[
                { label: 'Data', value: formData.dataAvaliacao ? formatDate(formData.dataAvaliacao) : '—' },
                { label: 'Queixa Principal', value: formData.queixaPrincipal || '—' },
                { label: 'Diagnóstico', value: formData.diagnosticoFisioterapeutico || '—' },
              ].map(({ label, value }) => (
                <div key={label} className="flex gap-2 text-sm">
                  <span className="font-medium text-blue-700 w-36 shrink-0">{label}:</span>
                  <span className="text-blue-900 line-clamp-2">{value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Ficha de Avaliação Fisioterapêutica</h1>
        <p className="text-gray-500 mt-1">Registre todas as informações da avaliação do paciente</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-72 shrink-0 space-y-4">

          {/* Card: Paciente */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            {paciente ? (
              <>
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900 leading-tight">{paciente.nome}</p>
                      <span className="text-xs text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Ativo</span>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2"><Phone size={13} className="text-gray-400" />{formatPhone(paciente.telefone)}</div>
                    {idade !== null && (
                      <div className="flex items-center gap-2"><Calendar size={13} className="text-gray-400" />{idade} anos · {formatDate(paciente.dateOfBirth)}</div>
                    )}
                    {paciente.diagnosisTitle && (
                      <div className="flex items-center gap-2"><Activity size={13} className="text-gray-400" />Diagnóstico: {paciente.diagnosisTitle}</div>
                    )}
                  </div>
                </div>
                <div className="border-t border-gray-100 px-4 py-2">
                  <button
                    onClick={() => { setPacienteSelecionado(null); limparFormulario(); setSearchTerm(''); }}
                    className="w-full text-xs text-gray-400 hover:text-red-500 flex items-center justify-center gap-1 py-1 transition-colors"
                  >
                    <X size={12} /> Trocar paciente
                  </button>
                </div>
              </>
            ) : (
              <div className="p-4">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">Selecionar Paciente</h2>
                {/* Input com autocomplete — igual ao Dashboard */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Digite para buscar um paciente..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                  />
                  {searchTerm.trim() !== '' && (
                    <ul className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {filteredPacientes.length > 0 ? (
                        filteredPacientes.map(p => (
                          <li
                            key={p._id}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 text-sm"
                            onClick={() => handleSelecionarPaciente(p._id)}
                          >
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <p className="font-medium text-gray-900 truncate">{p.nome}</p>
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-3 text-gray-500 text-sm">Nenhum paciente encontrado.</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Card: Histórico */}
          {pacienteSelecionado && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-2"><FileText size={15} /> Histórico de Avaliações</p>
                <button onClick={limparFormulario} className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors" title="Nova avaliação">
                  <Plus size={13} />
                </button>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {avaliacoes.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">Nenhuma avaliação registrada</p>
                ) : avaliacoes.map(av => (
                  <div key={av._id}
                    className={`border rounded-xl p-3 text-xs transition-all ${avaliacaoSelecionada?._id === av._id ? 'border-blue-400 bg-blue-50' : 'border-gray-100 hover:border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-800">{formatDate(av.dataAvaliacao)}</span>
                      {avaliacaoSelecionada?._id === av._id && <span className="text-blue-600 text-xs font-medium">Atual</span>}
                    </div>
                    {av.queixaPrincipal && <p className="text-gray-500 truncate mb-2">{av.queixaPrincipal}</p>}
                    <div className="flex gap-1">
                      <button onClick={() => handleEditar(av)} className="flex-1 flex items-center justify-center gap-1 bg-white border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 px-2 py-1 rounded-lg transition-colors">
                        <Edit3 size={11} /> Editar
                      </button>
                      <button onClick={() => handleDeletar(av._id)} className="flex-1 flex items-center justify-center gap-1 bg-white border border-gray-200 text-gray-600 hover:border-red-400 hover:text-red-600 px-2 py-1 rounded-lg transition-colors">
                        <Trash2 size={11} /> Deletar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {!pacienteSelecionado ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                <User size={28} className="text-blue-400" />
              </div>
              <p className="text-lg font-semibold text-gray-700">Selecione um paciente</p>
              <p className="text-sm text-gray-400 mt-1">Escolha um paciente no painel lateral para iniciar a avaliação</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="border-b border-gray-100 px-8 pt-6 pb-0">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{editando ? 'Editar Avaliação' : 'Nova Avaliação'}</h2>
                    <p className="text-sm text-gray-400">Registre todas as informações da avaliação do paciente</p>
                  </div>
                  {editando && (
                    <button onClick={limparFormulario} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors">
                      <X size={15} /> Cancelar edição
                    </button>
                  )}
                </div>

                {/* Stepper */}
                <div className="flex items-center gap-0">
                  {STEPS.map((s, i) => (
                    <React.Fragment key={s.id}>
                      <button type="button" onClick={() => setStep(s.id)} className="flex flex-col items-center gap-1.5 group pb-4 relative">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all
                          ${step === s.id ? 'bg-blue-600 text-white shadow-md shadow-blue-200' :
                            isStepDone(s.id) ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                          {isStepDone(s.id) ? <Check size={14} /> : s.id}
                        </div>
                        <span className={`text-xs whitespace-nowrap font-medium transition-colors
                          ${step === s.id ? 'text-blue-600' : isStepDone(s.id) ? 'text-blue-400' : 'text-gray-400'}`}>
                          {s.label}
                        </span>
                        {step === s.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />}
                      </button>
                      {i < STEPS.length - 1 && (
                        <div className={`flex-1 h-px mb-5 mx-1 ${isStepDone(s.id) ? 'bg-blue-200' : 'bg-gray-200'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSalvar}>
                <div className="px-8 py-6 min-h-80">{renderStepContent()}</div>
                <div className="border-t border-gray-100 px-8 py-4 flex items-center justify-between bg-gray-50">
                  <button type="button" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                    <ChevronLeft size={16} /> Anterior
                  </button>
                  {step < STEPS.length ? (
                    <button type="button" onClick={() => setStep(s => Math.min(STEPS.length, s + 1))}
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm">
                      Próximo passo <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button type="submit" disabled={loading}
                      className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50">
                      <Save size={16} />
                      {loading ? 'Salvando...' : editando ? 'Atualizar Avaliação' : 'Salvar Avaliação'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputClass = "mt-1 block w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-shadow";

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
      <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">{icon}</div>
      <div>
        <p className="font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}